import { html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { BaseElement } from '../base/base-element.js';
import { formatLoopRange } from '../../loop.js';

/**
 * A saved loop definition for the current slice. Boundaries are display ticks
 * (the same on every track; see public/shared/loop-ticks.ts), with the measure
 * indexes kept for the list label and sort order. Mirrors the server `Loop`
 * shape, minus timestamps the list does not render.
 */
export interface SavedLoop {
    id: number;
    name: string;
    startMeasureIndex: number;
    endMeasureIndex: number;
    /** Inclusive display tick. */
    startTick: number;
    /** Exclusive display tick. */
    endTick: number;
}

/**
 * Popover for managing a user's saved loop regions for the current slice.
 *
 * Pure UI: it holds no persistence logic. The host (conductor) owns all HTTP
 * and loop state and passes the list down via `loops`. The component emits
 * intents; the host performs them and pushes updated `loops` back.
 *
 * @fires loop-select      - detail: { loop: SavedLoop }  (activate this loop)
 * @fires loop-rename      - detail: { id: number, name: string }
 * @fires loop-delete      - detail: { id: number }
 * @fires loop-save-new    - (save the current active region as a new loop)
 * @fires loop-save-update - detail: { id: number }  (overwrite the active loop's boundaries)
 */
@customElement('saved-loops')
export class SavedLoops extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-block;
                position: relative;
            }

            :host(:not([visible])) {
                display: none;
            }

            :host([disabled]) {
                opacity: 0.5;
                pointer-events: none;
            }

            .trigger {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 40px;
                height: 40px;
                padding: 0;
                border: 1px solid var(--border, #475569);
                border-radius: 50%;
                background: var(--surface, #1e293b);
                color: var(--text-primary, #f1f5f9);
                font-size: 1.5rem;
                font-weight: 600;
                cursor: pointer;
                transition: background 0.2s;
            }

            .trigger:hover,
            .trigger.active {
                background: var(--surface-light, #334155);
            }

            .panel {
                position: absolute;
                z-index: 99;
                top: 100%;
                right: 0;
                margin-top: 0.35rem;
                /* 320px keeps the loop name readable next to two 44px
                   touch targets (280px left it clipped). */
                width: 320px;
                background: var(--surface, #1e293b);
                border: 2px solid var(--border, #475569);
                border-radius: 0.5rem;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
                padding: 0.5rem;
                display: none;
            }

            :host([open]) .panel {
                display: block;
            }

            .save-row {
                display: flex;
                gap: 0.4rem;
                padding: 0.25rem 0.25rem 0.5rem;
                border-bottom: 1px solid var(--border, #475569);
                margin-bottom: 0.4rem;
            }

            .save-row button {
                flex: 1;
                padding: 0.45rem 0.5rem;
                border: 1px solid var(--border, #475569);
                border-radius: 0.375rem;
                background: var(--surface-light, #334155);
                color: var(--text-primary, #f1f5f9);
                font-size: 0.75rem;
                font-weight: 600;
                cursor: pointer;
                transition: background 0.2s;
            }

            .save-row button.primary {
                background: var(--primary-color, #3b82f6);
                border-color: var(--primary-color, #3b82f6);
                color: white;
            }

            .save-row button:hover {
                filter: brightness(1.1);
            }

            .list {
                max-height: 45vh;
                overflow-y: auto;
            }

            .empty {
                color: var(--text-muted, #94a3b8);
                font-size: 0.8rem;
                text-align: center;
                padding: 1rem 0.5rem;
            }

            .row {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                padding: 0.4rem 0.5rem;
                border-radius: 0.375rem;
                transition: background 0.2s;
            }

            .row:hover {
                background: var(--surface-light, #334155);
            }

            .row.current {
                background: rgba(var(--primary-rgb, 59, 130, 246), 0.15);
            }

            .row-select {
                flex: 1;
                min-width: 0;
                text-align: left;
                background: none;
                border: none;
                cursor: pointer;
                color: var(--text-primary, #f1f5f9);
                font-size: 0.8rem;
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: 0.1rem;
                /* Match the icon buttons so the whole row is a comfortable
                   target, not just the glyphs. */
                min-height: 44px;
                touch-action: manipulation;
                -webkit-tap-highlight-color: transparent;
            }

            .row-name {
                font-weight: 600;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .row-range {
                font-size: 0.7rem;
                color: var(--text-muted, #94a3b8);
            }

            .icon-btn {
                background: none;
                border: none;
                cursor: pointer;
                color: var(--text-muted, #94a3b8);
                font-size: 1.15rem;
                /* 44x44 is the iOS HIG minimum touch target. These were ~21x20,
                   which is hard to hit on a phone. The glyph stays small; it is
                   the hit area that grows. */
                min-width: 44px;
                min-height: 44px;
                padding: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                border-radius: 8px;
                flex-shrink: 0;
                transition: color 0.2s, background 0.2s;
                touch-action: manipulation;
                -webkit-tap-highlight-color: transparent;
            }

            .icon-btn:hover {
                background: var(--surface, #1e293b);
                color: var(--text-primary, #f1f5f9);
            }

            .icon-btn.danger:hover {
                color: var(--error-color, #ef4444);
                background: rgba(239, 68, 68, 0.12);
            }

            .rename-input {
                flex: 1;
                min-width: 0;
                padding: 0.3rem 0.4rem;
                border: 1px solid var(--primary-color, #3b82f6);
                border-radius: 0.375rem;
                background: var(--surface, #1e293b);
                color: var(--text-primary, #f1f5f9);
                font-size: 0.8rem;
            }

            .confirm-text {
                flex: 1;
                font-size: 0.75rem;
                color: var(--text-primary, #f1f5f9);
            }
        `,
    ];

    @property({ type: Array })
    loops: SavedLoop[] = [];

    /** id of the loop currently loaded into the active region (null if ad hoc). */
    @property({ type: Number })
    currentSavedLoopId: number | null = null;

    /** Whether a loop region is currently active (enables the Save actions). */
    @property({ type: Boolean })
    canSave = false;

    @property({ type: Boolean, reflect: true })
    visible = false;

    @property({ type: Boolean, reflect: true })
    disabled = false;

    @property({ type: Boolean, reflect: true })
    open = false;

    @state()
    private _renamingId: number | null = null;

    @state()
    private _confirmingDeleteId: number | null = null;

    private _boundOutsideClick = this._onOutsideClick.bind(this);

    connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener('click', this._boundOutsideClick);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener('click', this._boundOutsideClick);
    }

    private _onOutsideClick(e: Event): void {
        if (!this.open) return;
        if (!e.composedPath().includes(this)) this._close();
    }

    private _toggle(e: Event): void {
        e.stopPropagation();
        this.open ? this._close() : (this.open = true);
    }

    private _close(): void {
        this.open = false;
        this._renamingId = null;
        this._confirmingDeleteId = null;
    }

    private _emit(type: string, detail?: unknown): void {
        this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
    }

    private _select(loop: SavedLoop): void {
        this._emit('loop-select', { loop });
        this._close();
    }

    private _saveNew(): void {
        this._emit('loop-save-new');
        this._close();
    }

    private _saveUpdate(): void {
        if (this.currentSavedLoopId === null) return;
        this._emit('loop-save-update', { id: this.currentSavedLoopId });
        this._close();
    }

    private _startRename(id: number, e: Event): void {
        e.stopPropagation();
        this._confirmingDeleteId = null;
        this._renamingId = id;
    }

    private _commitRename(id: number, e: Event): void {
        const value = (e.target as HTMLInputElement).value.trim();
        this._renamingId = null;
        const loop = this.loops.find(l => l.id === id);
        if (value && loop && value !== loop.name) {
            this._emit('loop-rename', { id, name: value });
        }
    }

    private _onRenameKey(id: number, e: KeyboardEvent): void {
        if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
            this._renamingId = null;
        }
    }

    private _startDelete(id: number, e: Event): void {
        e.stopPropagation();
        this._renamingId = null;
        this._confirmingDeleteId = id;
    }

    private _confirmDelete(id: number, e: Event): void {
        e.stopPropagation();
        this._confirmingDeleteId = null;
        this._emit('loop-delete', { id });
    }

    private _cancelDelete(e: Event): void {
        e.stopPropagation();
        this._confirmingDeleteId = null;
    }

    private _renderSaveRow() {
        if (!this.canSave) return nothing;
        const hasCurrent = this.currentSavedLoopId !== null;
        return html`
            <div class="save-row">
                ${hasCurrent
                    ? html`
                          <button class="primary" @click=${this._saveUpdate}>Update</button>
                          <button @click=${this._saveNew}>Save as new</button>
                      `
                    : html`<button class="primary" @click=${this._saveNew}>Save current loop</button>`}
            </div>
        `;
    }

    private _renderRow(loop: SavedLoop) {
        const range = formatLoopRange(loop.startMeasureIndex, loop.endMeasureIndex);
        const isCurrent = loop.id === this.currentSavedLoopId;

        if (this._renamingId === loop.id) {
            return html`
                <div class="row">
                    <input
                        class="rename-input"
                        .value=${loop.name}
                        @keydown=${(e: KeyboardEvent) => this._onRenameKey(loop.id, e)}
                        @blur=${(e: Event) => this._commitRename(loop.id, e)}
                        ${ref(autofocus)}
                    />
                </div>
            `;
        }

        if (this._confirmingDeleteId === loop.id) {
            return html`
                <div class="row">
                    <span class="confirm-text">Delete “${loop.name}”?</span>
                    <button class="icon-btn danger" title="Confirm delete" @click=${(e: Event) => this._confirmDelete(loop.id, e)}>&check;</button>
                    <button class="icon-btn" title="Cancel" @click=${this._cancelDelete}>&times;</button>
                </div>
            `;
        }

        return html`
            <div class="row ${isCurrent ? 'current' : ''}">
                <button class="row-select" @click=${() => this._select(loop)}>
                    <span class="row-name">${loop.name}</span>
                    <span class="row-range">${range}</span>
                </button>
                <button class="icon-btn" title="Rename" @click=${(e: Event) => this._startRename(loop.id, e)}>&#9998;</button>
                <button class="icon-btn danger" title="Delete" @click=${(e: Event) => this._startDelete(loop.id, e)}>&times;</button>
            </div>
        `;
    }

    render() {
        const count = this.loops.length;
        return html`
            <button
                class="trigger ${this.open ? 'active' : ''}"
                @click=${this._toggle}
                aria-expanded=${this.open}
                aria-haspopup="menu"
                title="Saved loops"
            >
                <span>&#128257;</span>
            </button>
            <div class="panel" role="menu">
                ${this._renderSaveRow()}
                <div class="list">
                    ${count === 0
                        ? html`<p class="empty">No saved loops for this slice yet.</p>`
                        : this.loops.map(l => this._renderRow(l))}
                </div>
            </div>
        `;
    }
}

// Lit directive-free autofocus: focus the rename input once rendered.
const autofocus = (el?: Element) => {
    if (el instanceof HTMLInputElement) {
        requestAnimationFrame(() => {
            el.focus();
            el.select();
        });
    }
};

declare global {
    interface HTMLElementTagNameMap {
        'saved-loops': SavedLoops;
    }
}
